import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
  User,
  Service,
  Booking,
  AdminUser,
  ApiResponse,
  BookingFilters,
  UserFilters,
  ServiceFilters,
  ServiceForm,
  Professional,
  ProfessionalAvailability,
  EarningRecord,
  BlogPost,
  BlogForm,
  Notification,
  NotificationPreference,
  NotificationType,
  AppContent,
  AppContentKey,
  Banner,
  Coupon,
  BlockedSlotRecord,
  SlideshowItem,
  RevenueData,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7207/api';

// Create axios instance with interceptors
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Don't auto-logout on 401 errors - let components handle it if needed
    return Promise.reject(error);
  }
);

// Generic API call function
async function apiCall<T>(config: any): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient(config);
    const responseData = response.data;

    // Backend typically returns { success: true/false, data: ..., message: ..., error: ... }
    // Check if backend explicitly returned success: false
    if (responseData && typeof responseData === 'object' && responseData.success === false) {
      return {
        success: false,
        data: null as T,
        error: responseData.error || responseData.message || 'Request failed',
      };
    }

    // If status is not 2xx, treat as error (shouldn't happen if backend returns success: false)
    if (response.status < 200 || response.status >= 300) {
      return {
        success: false,
        data: null as T,
        error: responseData?.error || responseData?.message || `Request failed with status ${response.status}`,
      };
    }

    // Success case - extract data and message
    // Backend returns { success: true, data: ..., message: ... }
    // or sometimes just { data: ..., message: ... } without success field
    const data = responseData?.data !== undefined ? responseData.data : responseData;
    const message = responseData?.message;

    return {
      success: true,
      data: data as T,
      message: message,
    };
  } catch (error: any) {
    // For 401 errors, return error response without redirecting
    // The interceptor will handle redirect, but we prevent immediate redirect
    if (error.response?.status === 401) {
      return {
        success: false,
        data: null as T,
        error: error.response?.data?.message || 'Unauthorized. Please login again.',
      };
    }

    // Extract error message from various possible locations
    const errorMessage = error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Request failed';

    return {
      success: false,
      data: null as T,
      error: errorMessage,
    };
  }
}

function mapBackendUserToAdmin(u: any): User {
  const name = u?.name || u?.fullName || [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
  return {
    id: u?.id || u?._id,
    name: name || '',
    email: u?.email || '',
    phone: u?.phone || '',
    profileImage: u?.profilePicture || undefined,
    emailVerified: Boolean(u?.isEmailVerified),
    phoneVerified: Boolean(u?.isPhoneVerified),
    rating: typeof u?.averageRating === 'number' ? u.averageRating : undefined,
    freeCash: typeof u?.freeCashBalance === 'number' ? u.freeCashBalance : undefined,
    status: u?.isBlocked ? 'blocked' : (u?.isActive === false ? 'inactive' : 'active'),
    addresses: u?.addresses || [],
    lastLogins: u?.lastLogins || undefined,
    totalSpend: typeof u?.totalSpent === 'number' ? u.totalSpent : undefined,
    bookingsCount: typeof u?.totalBookings === 'number' ? u.totalBookings : undefined,
    signupLocation: u?.signupLocation || undefined,
    createdAt: u?.createdAt || new Date().toISOString(),
    updatedAt: u?.updatedAt || new Date().toISOString(),
  } as User;
}

export const adminAPI = {
  // Hubs
  getHubs: async (filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.set(key, String(value));
      });
    }
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/hubs?${params.toString()}`,
    });
  },

  createHub: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/hubs',
      data,
    });
  },

  updateHub: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/hubs/${id}`,
      data,
    });
  },

  deleteHub: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'DELETE',
      url: `/admin/hubs/${id}`,
    });
  },

  getHubWorkers: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/hubs/${id}/workers`,
    });
  },

  assignWorkersToHub: async (hubId: string, workerIds: string[]): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/hubs/${hubId}/workers`,
      data: { workerIds },
    });
  },

  removeWorkersFromHub: async (hubId: string, workerIds: string[]): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'DELETE',
      url: `/admin/hubs/${hubId}/workers`,
      data: { workerIds },
    });
  },

  // Auth
  authenticateAdmin: async (email: string, password: string): Promise<ApiResponse<AdminUser | null>> => {
    const response = await apiCall<AdminUser>({
      method: 'POST',
      url: '/admin/auth/login',
      data: { email, password },
    });

    if (response.success && response.data) {
      // Store token for future requests
      localStorage.setItem('admin_token', (response.data as any).token || '');
    }

    return response;
  },

  logout: async (): Promise<ApiResponse<null>> => {
    await apiCall<null>({
      method: 'POST',
      url: '/admin/auth/logout',
    });
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    return { success: true, data: null };
  },

  getCurrentAdmin: async (): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'GET',
      url: '/admin/auth/me',
    });
  },

  // Dashboard Metrics
  getDashboardKPIs: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/metrics/kpis',
    });
  },

  getRevenueData: async (months: number = 12): Promise<ApiResponse<RevenueData[]>> => {
    return apiCall<RevenueData[]>({
      method: 'GET',
      url: `/admin/metrics/revenue?months=${months}`,
    });
  },

  getBookingStatusDistribution: async (range: string = '12m', dateRange?: { from: string; to: string }): Promise<ApiResponse<any>> => {
    let url = `/admin/metrics/booking-status?range=${range}`;
    if (range === 'custom' && dateRange?.from && dateRange?.to) {
      url += `&dateFrom=${dateRange.from}&dateTo=${dateRange.to}`;
    }
    return apiCall<any>({
      method: 'GET',
      url,
    });
  },

  getRecentBookings: async (limit: number = 10): Promise<ApiResponse<Booking[]>> => {
    return apiCall<Booking[]>({
      method: 'GET',
      url: `/admin/bookings?limit=${limit}&sort=-createdAt`,
    });
  },

  getSystemHealth: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/system/health',
    });
  },

  // Users
  searchUsers: async (query: string): Promise<ApiResponse<User[]>> => {
    const response = await adminAPI.getUsers({ q: query, limit: 100, includeIncomplete: true } as any);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.items || []
      };
    }
    return { success: false, data: [], error: response.error };
  },

  getUsers: async (filters: UserFilters): Promise<ApiResponse<{ items: User[], total: number }>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};
    if (f.q) params.set('q', String(f.q));
    if (f.isActive !== undefined) params.set('isActive', String(f.isActive));
    if (f.isBlocked !== undefined) params.set('isBlocked', String(f.isBlocked));
    if (f.limit) params.set('limit', String(f.limit));
    if (f.skip) params.set('skip', String(f.skip));
    if (f.page) params.set('page', String(f.page));
    if (f.dateFrom) params.set('dateFrom', String(f.dateFrom));
    if (f.dateTo) params.set('dateTo', String(f.dateTo));
    if (f.includeIncomplete) params.set('includeIncomplete', String(f.includeIncomplete));

    const response = await apiCall<any>({
      method: 'GET',
      url: `/admin/users?${params.toString()}`,
    });

    // Handle both old format (array) and new format (object with items and total)
    let users: User[] = [];
    let total = 0;

    if (response.success) {
      if (Array.isArray(response.data)) {
        // Old format: array of users
        users = response.data.map(mapBackendUserToAdmin);
        total = users.length;
      } else if (response.data?.items && Array.isArray(response.data.items)) {
        // New format: object with items and total
        users = response.data.items.map(mapBackendUserToAdmin);
        total = response.data.total || users.length;
      } else {
        users = [];
        total = 0;
      }
    }

    // Always return consistent format: { items: User[], total: number }
    return {
      ...response,
      data: { items: users, total },
    };
  },

  getUserById: async (id: string): Promise<ApiResponse<User>> => {
    const response = await apiCall<User>({
      method: 'GET',
      url: `/admin/users/${id}?t=${Date.now()}`, // Add timestamp to bypass cache
    });

    if (response.success && response.data) {
      response.data = mapBackendUserToAdmin(response.data);
    }

    return response;
  },

  updateUser: async (id: string, userData: Partial<User>): Promise<ApiResponse<User>> => {
    const response = await apiCall<User>({
      method: 'PATCH',
      url: `/admin/users/${id}`,
      data: userData,
    });

    if (response.success && response.data) {
      response.data = mapBackendUserToAdmin(response.data);
    }

    return response;
  },

  deleteUser: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/users/${id}`,
    });
  },

  getUserWallet: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/users/${id}/wallet`,
    });
  },

  updateUserWallet: async (id: string, amount: number, reason: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PATCH',
      url: `/admin/users/${id}/wallet`,
      data: { amount, reason },
    });
  },

  // Enhanced wallet management functions
  creditUserWallet: async (id: string, amount: number, reason: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/users/${id}/wallet/adjust`,
      data: { amount, reason, type: 'credit' },
    });
  },

  debitUserWallet: async (id: string, amount: number, reason: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/users/${id}/wallet/adjust`,
      data: { amount, reason, type: 'debit' },
    });
  },

  getUserWalletHistory: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/users/${id}/wallet/history?t=${Date.now()}`, // Add timestamp to bypass cache
    });
  },

  // Rating management functions
  setUserRating: async (id: string, rating: number): Promise<ApiResponse<User>> => {
    const response = await apiCall<User>({
      method: 'PATCH',
      url: `/admin/users/${id}/rating`,
      data: { rating },
    });

    if (response.success && response.data) {
      response.data = mapBackendUserToAdmin(response.data);
    }

    return response;
  },

  getUserRatingDetails: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/users/${id}/rating-details`,
    });
  },

  resetUserRating: async (id: string): Promise<ApiResponse<User>> => {
    const response = await apiCall<User>({
      method: 'PATCH',
      url: `/admin/users/${id}/rating/reset`,
    });

    if (response.success && response.data) {
      response.data = mapBackendUserToAdmin(response.data);
    }

    return response;
  },

  getUserReferrals: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/users/${id}/referrals`,
    });
  },

  getUserBookings: async (id: string, status?: string): Promise<ApiResponse<Booking[]>> => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);

    return apiCall<Booking[]>({
      method: 'GET',
      url: `/admin/users/${id}/bookings?${params.toString()}`,
    });
  },

  getUserNotifications: async (id: string): Promise<ApiResponse<Notification[]>> => {
    return apiCall<Notification[]>({
      method: 'GET',
      url: `/admin/users/${id}/notifications`,
    });
  },

  exportUsers: async (format: string = 'csv', filters?: UserFilters): Promise<ApiResponse<Blob>> => {
    const params = new URLSearchParams();
    params.set('format', format);

    if (filters) {
      const f: any = filters;
      if (f.q) params.set('q', String(f.q));
      if (f.isActive !== undefined) params.set('isActive', String(f.isActive));
      if (f.isBlocked !== undefined) params.set('isBlocked', String(f.isBlocked));
      if (f.dateFrom) params.set('dateFrom', String(f.dateFrom));
      if (f.dateTo) params.set('dateTo', String(f.dateTo));
    }

    return apiCall<Blob>({
      method: 'GET',
      url: `/admin/users/export?${params.toString()}`,
      responseType: 'blob',
    });
  },

  bulkNotifyUsers: async (userIds: string[], title: string, message: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/users/notify',
      data: { userIds, title, message },
    });
  },

  removeUserProfilePicture: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'DELETE',
      url: `/admin/users/${id}/photo`,
    });
  },

  createUser: async (userData: { name: string; phone: string; email?: string; referralId?: string }): Promise<ApiResponse<User>> => {
    const response = await apiCall<User>({
      method: 'POST',
      url: '/admin/users',
      data: userData,
    });

    if (response.success && response.data) {
      response.data = mapBackendUserToAdmin(response.data);
    }

    return response;
  },

  // Bookings
  getBookings: async (filters?: BookingFilters): Promise<ApiResponse<Booking[]>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};
    if (f.status) params.set('status', f.status);
    if (f.userId) params.set('userId', f.userId);
    if (f.workerId) params.set('workerId', f.workerId);
    if (f.serviceId) params.set('serviceId', f.serviceId);
    if (f.dateFrom) params.set('dateFrom', f.dateFrom);
    if (f.dateTo) params.set('dateTo', f.dateTo);
    if (f.limit) params.set('limit', String(f.limit));
    if (f.skip) params.set('skip', String(f.skip));
    if (f.page) params.set('page', String(f.page));
    // Support both 'q' and 'search' for search parameter
    if (f.q) params.set('search', f.q);
    if (f.search) params.set('search', f.search);

    return apiCall<Booking[]>({
      method: 'GET',
      url: `/admin/bookings?${params.toString()}`,
    });
  },

  getBookingById: async (id: string): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'GET',
      url: `/admin/bookings/${id}`,
    });
  },

  getBookingChatMessages: async (id: string, page: number = 1, limit: number = 100): Promise<ApiResponse<{ messages: any[]; pagination: any }>> => {
    return apiCall<{ messages: any[]; pagination: any }>({
      method: 'GET',
      url: `/admin/bookings/${id}/chat?page=${page}&limit=${limit}`,
    });
  },

  updateBookingStatus: async (id: string, status: string, reason?: string): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'PATCH',
      url: `/admin/bookings/${id}/status`,
      data: { status, reason },
    });
  },

  updateBooking: async (id: string, data: any): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'PUT',
      url: `/admin/bookings/${id}`,
      data: data,
    });
  },

  cancelBooking: async (id: string, reason: string): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'POST',
      url: `/admin/bookings/${id}/cancel`,
      data: { reason },
    });
  },

  getAvailableWorkers: async (bookingId: string): Promise<ApiResponse<Professional[]>> => {
    // Note: backend reads booking details from database using bookingId
    return apiCall<Professional[]>({
      method: 'GET',
      url: `/admin/bookings/${bookingId}/available-workers`,
    });
  },

  assignWorker: async (bookingId: string, workerId: string): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'PUT',
      url: `/admin/bookings/${bookingId}/assign-worker`,
      data: { workerId },
    });
  },
  unassignWorker: async (bookingId: string): Promise<ApiResponse<Booking>> => {
    return apiCall<Booking>({
      method: 'DELETE',
      url: `/admin/bookings/${bookingId}/assign-worker`,
    });
  },

  exportBookings: async (format: string = 'csv', filters?: BookingFilters): Promise<ApiResponse<Blob>> => {
    const params = new URLSearchParams();
    if (filters) {
      const f: any = filters;
      // Map filter keys to backend expected parameter names
      if (f.status) params.set('status', String(f.status));
      if (f.userId) params.set('userId', String(f.userId));
      if (f.workerId) params.set('workerId', String(f.workerId));
      if (f.serviceId) params.set('serviceId', String(f.serviceId));
      if (f.dateFrom) params.set('dateFrom', String(f.dateFrom));
      if (f.dateTo) params.set('dateTo', String(f.dateTo));
      // Support both 'q' and 'search' for search parameter
      if (f.q) params.set('search', String(f.q));
      if (f.search) params.set('search', String(f.search));
    }
    params.set('format', format);

    try {
      const response = await apiClient.get(`/admin/bookings/export?${params.toString()}`, {
        responseType: 'blob',
      });

      // Check if response is an error (JSON blob)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          data: null as any,
          error: errorData.error || errorData.message || 'Export failed',
        };
      }

      // Success - return blob
      return {
        success: true,
        data: response.data as Blob,
      };
    } catch (error: any) {
      // Handle error response that might be JSON in blob format
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          data: null as any,
          error: errorData.error || errorData.message || 'Export failed',
        };
      }

      return {
        success: false,
        data: null as any,
        error: error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to export bookings',
      };
    }
  },

  bulkUpdateBookingStatus: async (bookingIds: string[], status: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/bookings/bulk/status',
      data: { bookingIds, status },
    });
  },

  reorderServices: async (items: { id: string; order: number }[], type: 'category' | 'popular' | 'mostBooked' = 'category'): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/services/reorder',
      data: { items, type },
    });
  },

  // Services
  getServices: async (filters?: ServiceFilters): Promise<ApiResponse<Service[]>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};
    if (f.category) params.set('category', f.category);
    if (f.popular !== undefined) params.set('popular', String(f.popular));
    if (f.mostBooked !== undefined) params.set('mostBooked', String(f.mostBooked));
    if (f.limit) params.set('limit', String(f.limit));
    if (f.page) params.set('page', String(f.page));
    if (f.search) params.set('search', f.search);
    if (f.view) params.set('view', f.view);

    return apiCall<Service[]>({
      method: 'GET',
      url: `/admin/services?${params.toString()}`,
    });
  },

  getServiceById: async (id: string): Promise<ApiResponse<Service>> => {
    return apiCall<Service>({
      method: 'GET',
      url: `/admin/services/${id}`,
    });
  },

  createService: async (payload: ServiceForm): Promise<ApiResponse<Service>> => {
    return apiCall<Service>({
      method: 'POST',
      url: '/admin/services',
      data: payload,
    });
  },

  updateService: async (id: string, payload: Partial<ServiceForm>): Promise<ApiResponse<Service>> => {
    return apiCall<Service>({
      method: 'PUT',
      url: `/admin/services/${id}`,
      data: payload,
    });
  },

  deleteService: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/services/${id}`,
    });
  },

  duplicateService: async (id: string): Promise<ApiResponse<Service>> => {
    return apiCall<Service>({
      method: 'POST',
      url: `/admin/services/${id}/duplicate`,
    });
  },

  toggleServiceStatus: async (id: string): Promise<ApiResponse<Service>> => {
    return apiCall<Service>({
      method: 'PUT',
      url: `/admin/services/${id}/toggle`,
    });
  },

  exportServices: async (format: string = 'csv', filters?: ServiceFilters): Promise<ApiResponse<Blob>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.set(key, String(value));
      });
    }
    params.set('format', format);

    return apiCall<Blob>({
      method: 'GET',
      url: `/services/export?${params.toString()}`,
      responseType: 'blob',
    });
  },

  uploadServiceImages: async (id: string, images: File[]): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    images.forEach((image, index) => {
      formData.append(`images`, image);
    });

    return apiCall<any>({
      method: 'POST',
      url: `/admin/services/${id}/images`,
      data: formData,
      // Don't set Content-Type header for FormData - let browser set it with boundary
    });
  },

  getServiceAnalytics: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/services/${id}/analytics`,
    });
  },

  getServiceBookings: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/services/${id}/bookings`,
    });
  },

  updateServiceAnalytics: async (id: string, analytics: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/services/${id}/analytics`,
      data: analytics,
    });
  },

  getServiceTrends: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/services/${id}/trends`,
    });
  },

  // Service Categories
  getServiceCategories: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/service-categories',
    });
  },

  uploadServiceCategoryImage: async (file: File): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('image', file);

    return apiCall<any>({
      method: 'POST',
      url: '/admin/service-categories/upload-image',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  uploadServiceImage: async (file: File): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/admin/services/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
      },
      body: formData,
    });

    const data = await response.json();
    return data;
  },

  // Service Rating Management
  updateServiceRating: async (serviceId: string, ratingData: {
    adminSetRating: number;
    adminSetReviewCount: number;
    isAdminControlled: boolean;
  }): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/services/${serviceId}/rating`,
      data: ratingData,
    });
  },

  getPendingReviews: async (serviceId: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/services/${serviceId}/pending-reviews`,
    });
  },

  getApprovedReviews: async (serviceId: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/services/${serviceId}/approved-reviews`,
    });
  },

  approveReview: async (serviceId: string, reviewId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/services/${serviceId}/reviews/${reviewId}/approve`,
    });
  },

  createServiceCategory: async (category: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/service-categories',
      data: category,
    });
  },

  updateServiceCategory: async (id: string, category: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/service-categories/${id}`,
      data: category,
    });
  },

  deleteServiceCategory: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/service-categories/${id}`,
    });
  },

  // Workers
  getWorkers: async (filters?: any): Promise<ApiResponse<Professional[]>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};
    if (f.status) params.set('status', f.status);
    if (f.limit) params.set('limit', String(f.limit));
    if (f.skip !== undefined) params.set('skip', String(f.skip));
    if (f.page) params.set('page', String(f.page));
    if (f.search) params.set('search', f.search);
    if (f.q) params.set('q', f.q);
    if (f.isActive !== undefined) params.set('isActive', String(f.isActive));
    if (f.isAvailable !== undefined) params.set('isAvailable', String(f.isAvailable));

    return apiCall<Professional[]>({
      method: 'GET',
      url: `/admin/workers?${params.toString()}`,
    });
  },

  getWorkerById: async (id: string): Promise<ApiResponse<Professional>> => {
    return apiCall<Professional>({
      method: 'GET',
      url: `/admin/workers/${id}`,
    });
  },

  updateWorker: async (id: string, workerData: Partial<Professional>): Promise<ApiResponse<Professional>> => {
    return apiCall<Professional>({
      method: 'PUT',
      url: `/admin/workers/${id}`,
      data: workerData,
    });
  },

  deleteWorker: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/workers/${id}`,
    });
  },

  approveWorker: async (id: string): Promise<ApiResponse<Professional>> => {
    return apiCall<Professional>({
      method: 'POST',
      url: `/admin/workers/${id}/approve`,
    });
  },

  rejectWorker: async (id: string, reason: string): Promise<ApiResponse<Professional>> => {
    return apiCall<Professional>({
      method: 'POST',
      url: `/admin/workers/${id}/reject`,
      data: { reason },
    });
  },

  getWorkerDocuments: async (workerId: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/workers/${workerId}/documents`,
    });
  },

  getWorkerStats: async (workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${workerId}/stats`,
    });
  },

  getWorkerAvailability: async (workerId: string, month: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${workerId}/availability?month=${month}`,
    });
  },

  getWorkerPayouts: async (pendingOnly: boolean = false): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (pendingOnly) params.set('pending', 'true');

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/workers/payouts?${params.toString()}`,
    });
  },

  markWorkerPaymentSent: async (workerId: string, amount: number): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${workerId}/mark-payment-sent`,
      data: { amount },
    });
  },

  requestWorkerMoreInfo: async (workerId: string, message: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${workerId}/request-info`,
      data: { message },
    });
  },

  adjustWorkerEarnings: async (workerId: string, amount: number, reason: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${workerId}/adjust-earnings`,
      data: { amount, reason },
    });
  },

  exportWorkerPayments: async (format: string, filters?: any): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};

    if (f.q) params.set('q', f.q);
    if (f.status) params.set('status', f.status);
    if (f.workerId) params.set('workerId', f.workerId);

    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/payouts/export?format=${format}&${params.toString()}`,
    });
  },

  exportWorkerPayouts: async (format: string = 'csv'): Promise<ApiResponse<Blob>> => {
    return apiCall<Blob>({
      method: 'GET',
      url: `/admin/workers/payouts/export?format=${format}`,
      responseType: 'blob',
    });
  },

  // Moderation (Ratings & Testimonials)
  getPendingModeration: async (type?: string): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/moderation/pending?${params.toString()}`,
    });
  },

  approveModerationItem: async (itemId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/moderation/${itemId}/approve`,
    });
  },

  rejectModerationItem: async (itemId: string, reason?: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/moderation/${itemId}/reject`,
      data: { reason },
    });
  },

  editTestimonial: async (id: string, text: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/testimonials/${id}`,
      data: { text },
    });
  },

  getApprovedModeration: async (type?: string): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/moderation/approved?${params.toString()}`,
    });
  },

  deleteModerationItem: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/moderation/${id}`,
    });
  },

  addAdminContent: async (content: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/moderation/admin-add',
      data: content,
    });
  },

  removeApprovedContent: async (itemId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/moderation/${itemId}/remove`,
    });
  },

  getApprovedContent: async (type?: string): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/moderation/approved?${params.toString()}`,
    });
  },

  // Blog Management
  getBlogPosts: async (filters?: any): Promise<ApiResponse<BlogPost[]>> => {
    const params = new URLSearchParams();
    const f: any = filters || {};
    if (f.status) params.set('status', f.status);
    if (f.category) params.set('category', f.category);
    if (f.limit) params.set('limit', String(f.limit));
    if (f.page) params.set('page', String(f.page));
    if (f.search) params.set('search', f.search);

    return apiCall<BlogPost[]>({
      method: 'GET',
      url: `/admin/blogs?${params.toString()}`,
    });
  },

  getBlogPostById: async (id: string): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>({
      method: 'GET',
      url: `/admin/blogs/${id}`,
    });
  },

  exportBlogPosts: async (format: string = 'csv', filters?: any): Promise<ApiResponse<Blob>> => {
    const params = new URLSearchParams();
    if (filters) {
      const f: any = filters;
      if (f.status) params.set('status', String(f.status));
      if (f.category) params.set('category', String(f.category));
      // Support both 'q' and 'search' for search parameter
      if (f.q) params.set('search', String(f.q));
      if (f.search) params.set('search', String(f.search));
    }
    params.set('format', format);

    try {
      const response = await apiClient.get(`/admin/blogs/export?${params.toString()}`, {
        responseType: 'blob',
      });

      // Check if response is an error (JSON blob)
      if (response.data instanceof Blob && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          data: null as any,
          error: errorData.error || errorData.message || 'Export failed',
        };
      }

      // Success - return blob
      return {
        success: true,
        data: response.data as Blob,
      };
    } catch (error: any) {
      // Handle error response that might be JSON in blob format
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        return {
          success: false,
          data: null as any,
          error: errorData.error || errorData.message || 'Export failed',
        };
      }

      return {
        success: false,
        data: null as any,
        error: error?.response?.data?.error || error?.message || 'Export failed',
      };
    }
  },

  createBlogPost: async (payload: BlogForm): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>({
      method: 'POST',
      url: '/admin/blogs',
      data: payload,
    });
  },

  updateBlogPost: async (id: string, payload: Partial<BlogForm>): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>({
      method: 'PUT',
      url: `/admin/blogs/${id}`,
      data: payload,
    });
  },

  deleteBlogPost: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/blogs/${id}`,
    });
  },

  uploadBlogImage: async (file: File): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/admin/blogs/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
      },
      body: formData,
    });

    const data = await response.json();
    return data;
  },

  duplicateBlogPost: async (id: string): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>({
      method: 'POST',
      url: `/admin/blogs/${id}/duplicate`,
    });
  },

  toggleBlogPostStatus: async (id: string): Promise<ApiResponse<BlogPost>> => {
    return apiCall<BlogPost>({
      method: 'PUT',
      url: `/admin/blogs/${id}/toggle`,
    });
  },

  updateBlogAnalytics: async (id: string, analytics: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/blogs/${id}/analytics`,
      data: analytics,
    });
  },

  getBlogAnalytics: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/blogs/${id}/analytics`,
    });
  },

  getBlogTrends: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/blogs/${id}/trends`,
    });
  },

  overrideBlogAnalytics: async (id: string, data: { views?: number; rating?: number }): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/blogs/${id}/override-analytics`,
      data,
    });
  },

  overrideBlogStats: async (id: string, data: { views?: number; likes?: number }): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/blogs/${id}/override-stats`,
      data,
    });
  },

  resetBlogStats: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/blogs/${id}/reset-stats`,
    });
  },

  // Notifications
  getNotifications: async (filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/notifications?${params.toString()}`,
    });
  },

  getNotificationById: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/notifications/${id}`,
    });
  },

  createNotification: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/notifications',
      data,
    });
  },

  updateNotification: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/notifications/${id}`,
      data,
    });
  },

  deleteNotification: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/notifications/${id}`,
    });
  },

  sendNotification: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/notifications/${id}/send`,
    });
  },

  getNotificationTemplates: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/notification-templates',
    });
  },

  createNotificationTemplate: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/notification-templates',
      data,
    });
  },

  updateNotificationTemplate: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/notification-templates/${id}`,
      data,
    });
  },

  deleteNotificationTemplate: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/notification-templates/${id}`,
    });
  },

  exportNotifications: async (format: string, filters?: any): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    params.append('format', format);
    return apiCall<any>({
      method: 'GET',
      url: `/admin/notifications/export?${params.toString()}`,
    });
  },

  // Offers & Coupons
  getCoupons: async (filters?: any): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/coupons',
    });
  },

  getCouponById: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/coupons/${id}`,
    });
  },

  createCoupon: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/coupons',
      data,
    });
  },

  updateCoupon: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/coupons/${id}`,
      data,
    });
  },

  deleteCoupon: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/coupons/${id}`,
    });
  },

  toggleCouponStatus: async (id: string): Promise<ApiResponse<any>> => {
    const coupon = await apiCall<any>({
      method: 'GET',
      url: `/admin/coupons/${id}`,
    });
    if (!coupon.success) throw new Error('Failed to fetch coupon');

    return apiCall<any>({
      method: 'PUT',
      url: `/admin/coupons/${id}`,
      data: { isActive: !coupon.data.isActive },
    });
  },

  // Free Cash Offers
  getFreeCashOffers: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/free-cash-offers',
    });
  },

  getFreeCashOfferById: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/free-cash-offers/${id}`,
    });
  },

  createFreeCashOffer: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/free-cash-offers',
      data,
    });
  },

  updateFreeCashOffer: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/free-cash-offers/${id}`,
      data,
    });
  },

  deleteFreeCashOffer: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/free-cash-offers/${id}`,
    });
  },

  toggleFreeCashOfferStatus: async (id: string): Promise<ApiResponse<any>> => {
    const offer = await apiCall<any>({
      method: 'GET',
      url: `/admin/free-cash-offers/${id}`,
    });
    if (!offer.success) throw new Error('Failed to fetch offer');

    return apiCall<any>({
      method: 'PUT',
      url: `/admin/free-cash-offers/${id}`,
      data: { isActive: !offer.data.isActive },
    });
  },

  // Blog Categories
  getBlogCategories: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/blog-categories',
    });
  },

  createBlogCategory: async (category: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/blog-categories',
      data: category,
    });
  },

  updateBlogCategory: async (id: string, category: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/blog-categories/${id}`,
      data: category,
    });
  },

  deleteBlogCategory: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/blog-categories/${id}`,
    });
  },

  // Slideshow Management
  getSlideshow: async (): Promise<ApiResponse<SlideshowItem[]>> => {
    return apiCall<SlideshowItem[]>({
      method: 'GET',
      url: '/admin/slideshow',
    });
  },

  createSlide: async (slide: any): Promise<ApiResponse<SlideshowItem>> => {
    return apiCall<SlideshowItem>({
      method: 'POST',
      url: '/admin/slideshow',
      data: slide,
    });
  },

  updateSlide: async (id: string, slide: any): Promise<ApiResponse<SlideshowItem>> => {
    return apiCall<SlideshowItem>({
      method: 'PUT',
      url: `/admin/slideshow/${id}`,
      data: slide,
    });
  },

  deleteSlide: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/slideshow/${id}`,
    });
  },

  toggleSlideStatus: async (id: string): Promise<ApiResponse<SlideshowItem>> => {
    return apiCall<SlideshowItem>({
      method: 'PUT',
      url: `/admin/slideshow/${id}`,
      data: { isActive: true }, // This will need to be implemented properly
    });
  },

  reorderSlides: async (slideIds: string[]): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/slideshow/reorder',
      data: { slideIds },
    });
  },

  // Slideshow object for easier access
  slideshow: {
    getAll: async (search?: string, filter?: string): Promise<ApiResponse<SlideshowItem[]>> => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filter && filter !== 'all') params.set('filter', filter);

      return apiCall<SlideshowItem[]>({
        method: 'GET',
        url: `/admin/slideshow?${params.toString()}`,
      });
    },

    getById: async (id: string): Promise<ApiResponse<SlideshowItem>> => {
      return apiCall<SlideshowItem>({
        method: 'GET',
        url: `/admin/slideshow/${id}`,
      });
    },

    create: async (slide: any): Promise<ApiResponse<SlideshowItem>> => {
      return apiCall<SlideshowItem>({
        method: 'POST',
        url: '/admin/slideshow',
        data: slide,
      });
    },

    update: async (id: string, slide: any): Promise<ApiResponse<SlideshowItem>> => {
      return apiCall<SlideshowItem>({
        method: 'PUT',
        url: `/admin/slideshow/${id}`,
        data: slide,
      });
    },

    delete: async (id: string): Promise<ApiResponse<{ message: string }>> => {
      return apiCall<{ message: string }>({
        method: 'DELETE',
        url: `/admin/slideshow/${id}`,
      });
    },

    toggleStatus: async (id: string): Promise<ApiResponse<SlideshowItem>> => {
      return apiCall<SlideshowItem>({
        method: 'PUT',
        url: `/admin/slideshow/${id}`,
        data: { isActive: true }, // This will need to be implemented properly
      });
    },

    updateOrder: async (updates: { id: string; order: number }[]): Promise<ApiResponse<any>> => {
      return apiCall<any>({
        method: 'PUT',
        url: '/admin/slideshow/reorder',
        data: { updates },
      });
    },

    uploadImage: async (file: File): Promise<ApiResponse<{ imageUrl: string; filename: string; originalName: string; size: number }>> => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('uploadType', 'slideshow');

      const response = await fetch(`${API_BASE_URL}/admin/slideshow/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: formData,
      });

      const data = await response.json();
      return data;
    },
  },

  // Analytics & Reports
  getDashboard: async (range: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/dashboard?range=${range}`,
    });
  },

  getRevenueAnalytics: async (range: string, dateRange?: { from: string; to: string }): Promise<ApiResponse<any>> => {
    let url = `/admin/metrics/revenue?range=${range}`;
    if (range === 'custom' && dateRange?.from && dateRange?.to) {
      url += `&dateFrom=${dateRange.from}&dateTo=${dateRange.to}`;
    }
    return apiCall<any>({
      method: 'GET',
      url,
    });
  },

  getRevenueByService: async (range?: string): Promise<ApiResponse<any>> => {
    const params = range ? `?range=${range}` : '';
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/revenue/by-service${params}`,
    });
  },

  getRevenueByCity: async (range?: string): Promise<ApiResponse<any>> => {
    const params = range ? `?range=${range}` : '';
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/revenue/by-city${params}`,
    });
  },

  getBookingAnalytics: async (type: string, range?: string): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/bookings/${type}?${params.toString()}`,
    });
  },

  getUserAnalytics: async (type: string, range?: string): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/users/${type}?${params.toString()}`,
    });
  },

  getWorkerAnalytics: async (type: string, range?: string): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/workers/${type}?${params.toString()}`,
    });
  },

  getConversionFunnel: async (range?: string): Promise<ApiResponse<any>> => {
    const params = range ? `?range=${range}` : '';
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/conversion-funnel${params}`,
    });
  },

  getCustomerSegmentation: async (range?: string): Promise<ApiResponse<any>> => {
    const params = range ? `?range=${range}` : '';
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/customer-segmentation${params}`,
    });
  },

  getGeographicAnalytics: async (range?: string): Promise<ApiResponse<any>> => {
    const params = range ? `?range=${range}` : '';
    return apiCall<any>({
      method: 'GET',
      url: `/admin/analytics/geographic${params}`,
    });
  },

  getRealTimeMetrics: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/analytics/real-time',
    });
  },

  exportData: async (type: string, format: string, range?: string, customFrom?: string, customTo?: string, filters?: any): Promise<ApiResponse<Blob>> => {
    const params = new URLSearchParams();
    params.set('type', type);
    params.set('format', format);
    if (range) params.set('range', range);
    if (customFrom) params.set('customFrom', customFrom);
    if (customTo) params.set('customTo', customTo);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }

    return apiCall<Blob>({
      method: 'POST',
      url: `/admin/exports?${params.toString()}`,
      responseType: 'blob',
    });
  },

  scheduleReport: async (schedule: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/exports/schedule',
      data: schedule,
    });
  },

  getScheduledReports: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/exports/scheduled',
    });
  },

  updateScheduledReport: async (id: string, schedule: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/exports/scheduled/${id}`,
      data: schedule,
    });
  },

  deleteScheduledReport: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/exports/scheduled/${id}`,
    });
  },

  getExportHistory: async (filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/exports/history?${params.toString()}`,
    });
  },

  // Settings
  getAllSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings',
    });
  },

  updateSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings',
      data: settings,
    });
  },

  getGeneralSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/general',
    });
  },

  updateGeneralSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/general',
      data: settings,
    });
  },

  getEmailSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/email',
    });
  },

  updateEmailSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/email',
      data: settings,
    });
  },

  testEmailSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/settings/email/test',
    });
  },

  getSMSSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/sms',
    });
  },

  updateSMSSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/sms',
      data: settings,
    });
  },

  testSMSSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/settings/sms/test',
    });
  },

  getPaymentSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/payments',
    });
  },

  updatePaymentSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/payments',
      data: settings,
    });
  },

  getSecuritySettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/security',
    });
  },

  updateSecuritySettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/security',
      data: settings,
    });
  },

  getBusinessSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/business',
    });
  },

  updateBusinessSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/business',
      data: settings,
    });
  },

  getIntegrationSettings: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/settings/integrations',
    });
  },

  updateIntegrationSettings: async (settings: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/settings/integrations',
      data: settings,
    });
  },

  testIntegration: async (type: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/settings/integrations/test/${type}`,
    });
  },

  // Admin Management
  getAdminAccounts: async (search?: string, role?: string): Promise<ApiResponse<AdminUser[]>> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (role && role !== 'all') params.set('role', role);
    return apiCall<AdminUser[]>({
      method: 'GET',
      url: `/admin/admins?${params.toString()}`,
    });
  },

  getAdminAccountById: async (id: string): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'GET',
      url: `/admin/admins/${id}`,
    });
  },

  createAdminAccount: async (admin: any): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'POST',
      url: '/admin/admins',
      data: admin,
    });
  },

  updateAdminAccount: async (id: string, admin: any): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'PUT',
      url: `/admin/admins/${id}`,
      data: admin,
    });
  },

  deleteAdminAccount: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/admins/${id}`,
    });
  },

  toggleAdminAccountStatus: async (id: string): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'POST',
      url: `/admin/admins/${id}/toggle`,
    });
  },

  resetAdminPassword: async (id: string): Promise<ApiResponse<{ message: string; temporaryPassword: string }>> => {
    return apiCall<{ message: string; temporaryPassword: string }>({
      method: 'POST',
      url: `/admin/admins/${id}/reset-password`,
    });
  },

  updateAdminRole: async (id: string, role: string, syncPermissions?: boolean): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'PUT',
      url: `/admin/admins/${id}/role`,
      data: { role, syncPermissions },
    });
  },

  updateAdminPassword: async (id: string, password: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'PUT',
      url: `/admin/admins/${id}/password`,
      data: { password },
    });
  },

  updateAdminPermissions: async (id: string, permissions: string[]): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'PUT',
      url: `/admin/admins/${id}/permissions`,
      data: { permissions },
    });
  },

  // Admin Activity Logs
  getAdminsForLogs: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/activity-logs/admins',
    });
  },

  getAdminActivityLogs: async (adminId: string, filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/activity-logs/admins/${adminId}/logs?${params.toString()}`,
    });
  },

  getAllActivityLogs: async (filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/activity-logs/logs?${params.toString()}`,
    });
  },

  // Legacy methods for backward compatibility
  getAdmins: async (): Promise<ApiResponse<AdminUser[]>> => {
    return apiCall<AdminUser[]>({
      method: 'GET',
      url: '/admin/admins',
    });
  },

  createAdmin: async (admin: any): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'POST',
      url: '/admin/admins',
      data: admin,
    });
  },

  updateAdmin: async (id: string, admin: any): Promise<ApiResponse<AdminUser>> => {
    return apiCall<AdminUser>({
      method: 'PUT',
      url: `/admin/admins/${id}`,
      data: admin,
    });
  },

  deleteAdmin: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/admins/${id}`,
    });
  },

  // Worker Approval Methods
  getPendingWorkers: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/workers/pending',
    });
  },

  // Customer Ratings Approval Methods
  getPendingRatings: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/ratings/pending',
    });
  },

  approveRating: async (ratingId: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'PUT',
      url: `/admin/ratings/${ratingId}/approve`,
    });
  },

  // Lead Management Methods
  getLeadsRequiringManualAssignment: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/bookings/leads/manual-assignment',
    });
  },

  getLeadStatistics: async (bookingId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/bookings/${bookingId}/lead-stats`,
    });
  },

  getAvailableWorkersForBooking: async (bookingId: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/bookings/${bookingId}/available-workers`,
    });
  },

  manuallyAssignWorker: async (bookingId: string, workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/bookings/${bookingId}/assign-worker`,
      data: { workerId },
    });
  },

  sendLeadToWorkers: async (bookingId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/bookings/${bookingId}/send-lead-to-workers`,
    });
  },

  sendLeadToWorker: async (bookingId: string, workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/bookings/${bookingId}/send-lead-to-worker/${workerId}`,
    });
  },

  retryAutoAssignment: async (bookingId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/bookings/${bookingId}/auto-assign`,
    });
  },

  // Service Reviews Approval Methods
  getPendingServiceReviews: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/service-reviews/pending',
    });
  },

  approveServiceReview: async (serviceId: string, reviewId: string): Promise<ApiResponse<{ approved: boolean; newRating: number; newReviewCount: number }>> => {
    return apiCall<{ approved: boolean; newRating: number; newReviewCount: number }>({
      method: 'PUT',
      url: `/admin/service-reviews/${serviceId}/${reviewId}/approve`,
    });
  },

  rejectRating: async (ratingId: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'PUT',
      url: `/admin/ratings/${ratingId}/reject`,
    });
  },

  // Booking Management Methods
  createBooking: async (bookingData: any): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'POST',
      url: '/admin/bookings',
      data: bookingData,
    });
  },

  // Referral Analytics
  getReferralsAnalytics: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/analytics/referrals',
    });
  },

  // Legacy methods for backward compatibility
  getPayments: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/payment/admin/list',
    });
  },
  updatePaymentStatus: async (id: string, status: string): Promise<ApiResponse<any>> => {
    // No server-side storage for statuses yet; return success so UI doesn't break
    return { success: true, data: { id, status } } as ApiResponse<any>;
  },
  refundPayment: async (paymentId: string, amount?: number, bookingId?: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/payment/refund',
      data: { payment_id: paymentId, amount, bookingId },
    });
  },
  getDashboardStats: async (): Promise<ApiResponse<any>> => ({ success: true, data: {} as any }),
  getReferrals: async (): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: '/admin/referrals',
    });
  },
  getTickets: async (): Promise<ApiResponse<any[]>> => ({ success: true, data: [] as any }),
  getTemplates: async (): Promise<ApiResponse<any[]>> => ({ success: true, data: [] as any }),
  getNotificationPreferences: async (): Promise<ApiResponse<NotificationPreference[]>> => ({ success: true, data: [] as any }),
  updateNotificationPreference: async (type: NotificationType, enabled: boolean): Promise<ApiResponse<NotificationPreference>> => ({ success: true, data: { type, enabled } as any }),
  getAppContent: async (_key: AppContentKey): Promise<ApiResponse<AppContent>> => ({ success: true, data: { key: _key, content: '', updatedAt: new Date().toISOString() } as any }),
  updateAppContent: async (key: AppContentKey, content: string): Promise<ApiResponse<AppContent>> => ({ success: true, data: { key, content, updatedAt: new Date().toISOString() } as any }),
  getBanners: async (): Promise<ApiResponse<Banner[]>> => ({ success: true, data: [] as any }),
  getBlockedSlots: async (_serviceId?: string, _date?: string): Promise<ApiResponse<BlockedSlotRecord[]>> => ({ success: true, data: [] as any }),
  getProfessionalAvailability: async (id: string): Promise<ApiResponse<any>> => {
    console.log('[API] Fetching professional availability for ID:', id);
    console.log('[API] Full URL will be:', `${API_BASE_URL}/admin/workers/${id}/availability`);
    const response = await apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${id}/availability`,
    });
    console.log('[API] getProfessionalAvailability response:', response);
    return response;
  },
  getProfessionalEarnings: async (id: string, month: string): Promise<ApiResponse<EarningRecord>> => {
    return apiCall<EarningRecord>({
      method: 'GET',
      url: `/admin/workers/${id}/earnings${month ? '?month=' + month : ''}`,
    });
  },

  // Workers/Professionals Management
  getProfessionals: async (filters?: { status?: string; limit?: number; skip?: number }): Promise<ApiResponse<Professional[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.skip) params.set('skip', String(filters.skip));

    return apiCall<Professional[]>({
      method: 'GET',
      url: `/admin/workers${params.toString() ? '?' + params.toString() : ''}`,
    });
  },
  getProfessionalById: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${id}?t=${Date.now()}`,
    });
  },

  createProfessional: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/workers',
      data,
    });
  },

  // Alias for createProfessional (backward compatibility)
  createWorker: async (data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/workers',
      data,
    });
  },

  updateProfessional: async (id: string, data: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}`,
      data,
    });
  },

  deleteProfessional: async (id: string): Promise<ApiResponse<{ message: string }>> => {
    return apiCall<{ message: string }>({
      method: 'DELETE',
      url: `/admin/workers/${id}`,
    });
  },

  approveProfessional: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}/approve`,
    });
  },

  rejectProfessional: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}/reject`,
    });
  },

  blockProfessional: async (id: string, isBlocked: boolean): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}/block`,
      data: { isBlocked },
    });
  },

  getProfessionalBookings: async (id: string, filters?: { status?: string; limit?: number; skip?: number }): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.skip) params.set('skip', String(filters.skip));

    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${id}/bookings${params.toString() ? '?' + params.toString() : ''}`,
    });
  },

  getProfessionalWallet: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/workers/${id}/wallet`,
    });
  },

  adjustProfessionalWallet: async (id: string, amount: number, type: 'credit' | 'debit', description: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${id}/wallet/adjust`,
      data: { amount, type, description },
    });
  },

  creditProfessionalWallet: async (id: string, amount: number, description: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${id}/wallet/adjust`,
      data: { amount, type: 'credit', description },
    });
  },

  debitProfessionalWallet: async (id: string, amount: number, description: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${id}/wallet/adjust`,
      data: { amount, type: 'debit', description },
    });
  },

  uploadProfessionalProfilePicture: async (id: string, file: File): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('profilePicture', file);

    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${id}/profile-picture`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  removeProfessionalProfilePicture: async (id: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'DELETE',
      url: `/admin/workers/${id}/profile-picture`,
    });
  },

  setProfessionalCommission: async (id: string, rate: number): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}`,
      data: { commissionRate: rate },
    });
  },

  updateProfessionalAvailability: async (id: string, availability: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/workers/${id}/availability`,
      data: { availability },
    });
  },

  // Worker Admin Ratings (Multi-Rating System - Permanent, No Deletion)
  getWorkerAdminRatings: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/workers/${id}/ratings/admin`,
    });
  },

  addWorkerAdminRating: async (id: string, rating: number): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/workers/${id}/ratings/admin`,
      data: { rating },
    });
  },

  // Service Admin Ratings (Multi-Rating System - Permanent, No Deletion)
  getServiceAdminRatings: async (id: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/services/${id}/ratings/admin`,
    });
  },

  addServiceAdminRating: async (id: string, rating: number): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/services/${id}/ratings/admin`,
      data: { rating },
    });
  },

  // Admin Preferences
  getAdminPreferences: async (): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: '/admin/preferences',
    });
  },

  updateAdminPreferences: async (preferences: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/preferences',
      data: preferences,
    });
  },

  updateSidebarPreferences: async (sidebar: { isVisible: boolean; itemOrder: string[] }): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: '/admin/preferences/sidebar',
      data: sidebar,
    });
  },

  // Hubs
  getHubs: async (filters?: any): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters?.city) params.append('city', filters.city);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.skip) params.append('skip', String(filters.skip));

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/hubs${params.toString() ? `?${params.toString()}` : ''}`,
    });
  },

  getHubById: async (hubId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/hubs/${hubId}`,
    });
  },

  createHub: async (hubData: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/hubs',
      data: hubData,
    });
  },

  updateHub: async (hubId: string, hubData: any): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/hubs/${hubId}`,
      data: hubData,
    });
  },

  deleteHub: async (hubId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'DELETE',
      url: `/admin/hubs/${hubId}`,
    });
  },

  getHubStatistics: async (hubId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/hubs/${hubId}/statistics`,
    });
  },

  getHubWorkers: async (hubId: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/hubs/${hubId}/workers`,
    });
  },

  assignWorkerToHub: async (hubId: string, workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/hubs/${hubId}/workers/${workerId}`,
    });
  },

  findHubByPincode: async (pincode: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/hubs/lookup/pincode/${pincode}`,
    });
  },

  // Complaints Management
  getComplaints: async (filters?: { status?: 'active' | 'closed'; userId?: string }): Promise<ApiResponse<any[]>> => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.userId) params.set('userId', filters.userId);

    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/complaints?${params.toString()}`,
    });
  },

  createComplaint: async (data: { userId: string; message: string }): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: '/admin/complaints',
      data,
    });
  },

  closeComplaint: async (complaintId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/complaints/${complaintId}/close`,
    });
  },

  reopenComplaint: async (complaintId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/complaints/${complaintId}/reopen`,
    });
  },

  // Location Tracking
  getWorkerLocationHistory: async (workerId: string, date: string): Promise<ApiResponse<any[]>> => {
    return apiCall<any[]>({
      method: 'GET',
      url: `/admin/worker/${workerId}/location-history?date=${date}`,
    });
  },

  getWorkerCurrentLocation: async (workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'GET',
      url: `/admin/worker/${workerId}/current-location`,
    });
  },

  triggerLocateWorker: async (workerId: string): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'POST',
      url: `/admin/worker/${workerId}/locate-now`,
    });
  },

  toggleWorkerTracking: async (workerId: string, enabled: boolean): Promise<ApiResponse<any>> => {
    return apiCall<any>({
      method: 'PUT',
      url: `/admin/worker/${workerId}/toggle-tracking`,
      data: { enabled },
    });
  },
};

export default adminAPI;